import { AxiosError } from 'axios';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTableSkeleton,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  OverflowMenu,
  OverflowMenuItem,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Button,
  Link,
  Modal,
  Toggle,
  ComboBox,
  Tag,
} from '@carbon/react';
import { Add } from '@carbon/react/icons';
import DeleteConfirmation from 'src/components/Base/DeleteConfirmation';
import EmptyState from 'src/components/Base/EmptyState';
import ImagesViewer from 'src/components/Base/ImagesViewer';
import MenuItemVariantForm from 'src/components/Menu/MenuItemVariantForm';
import { CropperModal } from 'src/components/Uploader/CropperModal';
import {
  useMenuItemVariants,
  useCreateMenuItemVariantsMutation,
  useUpdateMenuItemVariantsMutation,
  useDeleteMenuItemVariantsMutation,
} from 'src/hooks/useMenuItemVariants';
import { useMenuItems } from 'src/hooks/useMenuItems';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { MenuItemVariantEntity } from 'src/services/apis/types';
import { getImageUrl } from 'src/utils/cloudinary';
import { getMutationErrorMessage } from 'src/utils/error';
import { KToNumber, NumberToK } from 'src/utils/price';

const MenuItemVariantsSettings = React.memo(() => {
  const { t } = useTranslation();
  const formRef = useRef<any>();
  const currentRestaurant = useCurrentRestaurant();
  const { isLoading: isBranchesLoading, data: branches } = useBranches();
  const { isLoading: isMenuItemsLoading, data: menuItems } = useMenuItems();
  const { isLoading, data } = useMenuItemVariants();
  const createMenuItemVariantMutation = useCreateMenuItemVariantsMutation();
  const updateMenuItemVariantMutation = useUpdateMenuItemVariantsMutation();
  const deleteMenuItemVariantMutation = useDeleteMenuItemVariantsMutation();
  const [filters, setFilters] = useState({
    menuId: null,
    menuItemId: null,
    branchId: null,
  });
  const [openAddMenuItemVariant, setOpenAddMenuItemVariant] = useState(false);
  const [editingMenuItemVariant, setEditingMenuItemVariant] = useState<null | undefined | MenuItemVariantEntity>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [croppingFile, setCroppingFile] = useState<{ file: File; previewUrl: string } | null>(null);
  const [deletingMenuItemVariant, setDeletingMenuItemVariant] = useState<null | undefined | MenuItemVariantEntity>(
    null
  );
  const [viewingMenuItemVariantPhotos, setViewingMenuItemVariantPhotos] = useState<MenuItemVariantEntity[]>([]);

  const handleCloseAddOrEditMenuItemVariant = useCallback(() => {
    setOpenAddMenuItemVariant(false);
    setEditingMenuItemVariant(null);
    formRef.current?.resetForm();
  }, []);

  const handleAddMenuItemVariant = useCallback(() => {
    formRef.current?.resetForm();
    setOpenAddMenuItemVariant(true);
  }, []);

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      if (values.specificForBranchIds.length === 0 || !values.menuItemId) {
        return;
      }
      const payload = {
        ...values,
        restaurantId: currentRestaurant?.id,
        tags: values.tags?.split(',').map((tag) => tag.trim()) || [],
        photos: values.photo ? [values.photo] : [],
        price: KToNumber(parseInt(values.price, 10)),
        order: parseInt(values.order, 10),
        blurhashPhotos: values.photoBlurhash ? [values.photoBlurhash] : [],
      };
      if (editingMenuItemVariant) {
        await updateMenuItemVariantMutation.mutateAsync(payload);
      } else {
        await createMenuItemVariantMutation.mutateAsync(payload);
      }
      handleCloseAddOrEditMenuItemVariant();
    },
    [currentRestaurant?.id, editingMenuItemVariant]
  );

  const handleAbortDelete = useCallback(() => {
    setDeletingMenuItemVariant(null);
  }, []);

  const handleDelete = useCallback(async () => {
    await deleteMenuItemVariantMutation.mutateAsync(deletingMenuItemVariant?.id!);
    setDeletingMenuItemVariant(null);
  }, [deletingMenuItemVariant]);

  const handleCropped = useCallback(({ fileId, format, blurhash }) => {
    setCroppingFile(null);
    formRef.current?.setFieldValue('photo', `${fileId}.${format}`);
    formRef.current?.setFieldValue('photoBlurhash', blurhash);
  }, []);

  const menuItemSelectionItems = useMemo(() => {
    return menuItems?.data?.map((item) => ({ id: item.id, text: item.name }));
  }, [menuItems?.data]);

  const branchSelectionItems = useMemo(() => {
    return branches?.data?.map((item) => ({ id: item.id, text: item.name }));
  }, [branches?.data]);

  const headers = [
    {
      key: 'name',
      header: t('common.name'),
    },
    {
      key: 'sku',
      header: t('common.sku'),
    },
  ];

  const extraHeaders = [
    { key: 'menu', header: t('common.menu') },
    { key: 'price', header: t('common.price') },
    { key: 'photo', header: t('common.photo') },
    { key: 'branches', header: t('common.branches') },
    { key: 'available', header: t('common.available') },
    { key: 'actions', header: t('common.actions') },
  ];

  if (!currentRestaurant || isLoading || isMenuItemsLoading || isBranchesLoading) {
    return (
      <DataTableSkeleton
        columnCount={headers.length + extraHeaders.length}
        rowCount={6}
        headers={[...headers, ...extraHeaders]}
      />
    );
  }

  const filteredRows =
    data?.data
      .filter((item) => {
        if (filters?.menuItemId) {
          return item.menuItemId === filters?.menuItemId;
        }
        return true;
      })
      .filter((item) => {
        if (filters?.branchId) {
          return item.specificForBranchIds.includes(filters?.branchId);
        }
        return true;
      })
      .filter((item) => {
        if (searchQuery) {
          return (
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.sku.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        return true;
      })
      .map((item) => {
        return {
          ...item,
          branches: branches?.data
            ?.filter((branch) => item.specificForBranchIds.includes(branch.id))
            .map((branch) => branch.name),
        };
      }) || [];

  const submitting = createMenuItemVariantMutation.isLoading || updateMenuItemVariantMutation.isLoading;
  const createError = getMutationErrorMessage(createMenuItemVariantMutation.error as AxiosError);
  const updateError = getMutationErrorMessage(updateMenuItemVariantMutation.error as AxiosError);

  const tableTitle = t('titles.menuItemVariantsPage', { restaurantName: currentRestaurant?.name });

  return (
    <>
      <DataTable rows={filteredRows} headers={headers}>
        {({ rows, headers, getTableProps, getHeaderProps, getToolbarProps, getRowProps }) => (
          <TableContainer title={tableTitle} description={`${data?.total} ${t('common.variants')}`}>
            <TableToolbar {...getToolbarProps()} aria-label="data table toolbar">
              <TableToolbarContent>
                <TableToolbarSearch onChange={(e) => setSearchQuery(e.target.value)} />
                <ComboBox
                  items={menuItemSelectionItems}
                  placeholder={`${t('common.filterByMenuItem')}...`}
                  size={'lg'}
                  onChange={(e) => setFilters((current) => ({ ...current, menuItemId: e.selectedItem?.id }))}
                  className={'cds--combo-box--no-border-bottom'}
                  itemToString={(item) => (item ? item.text : '')}
                />
                <ComboBox
                  items={branchSelectionItems}
                  placeholder={`${t('common.filterByMenuItem')}...`}
                  size={'lg'}
                  onChange={(e) => setFilters((current) => ({ ...current, branchId: e.selectedItem?.id }))}
                  className={'cds--combo-box--no-border-bottom'}
                  itemToString={(item) => (item ? item.text : '')}
                />
                <Button renderIcon={Add} onClick={handleAddMenuItemVariant}>
                  {t('actions.create')} {t('common.variant')}
                </Button>
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {[...headers, ...extraHeaders].map((header) => (
                    <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow {...getRowProps({ row })}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                    <TableCell>
                      {menuItemSelectionItems?.find((item) => item.id === filteredRows[index]?.menuItemId)?.text}
                    </TableCell>
                    <TableCell>{NumberToK(filteredRows[index]?.price)}K</TableCell>
                    <TableCell>
                      {filteredRows[index]?.photos?.length
                        ? filteredRows[index]?.photos && (
                            <Link onClick={() => setViewingMenuItemVariantPhotos([filteredRows[index]])}>
                              {filteredRows[index]?.photos[0]}
                            </Link>
                          )
                        : t('messages.noPhotos')}
                    </TableCell>
                    <TableCell>
                      {filteredRows[index]?.branches?.map((branch) => (
                        <Tag type={'green'}>{branch}</Tag>
                      ))}
                    </TableCell>
                    <TableCell>
                      {filteredRows[index] && (
                        <Toggle
                          labelA={t('common.no')}
                          labelB={t('common.yes')}
                          toggled={filteredRows[index].available}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <OverflowMenu size="md" flipped ariaLabel={t('actions.title')}>
                        <OverflowMenuItem
                          itemText={t('actions.edit')}
                          requireTitle
                          onClick={() => setEditingMenuItemVariant(filteredRows[index])}
                        />
                        <OverflowMenuItem
                          hasDivider
                          isDelete
                          itemText={t('actions.delete')}
                          onClick={() => setDeletingMenuItemVariant(filteredRows[index])}
                        />
                      </OverflowMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && <EmptyState />}
          </TableContainer>
        )}
      </DataTable>
      <Modal
        open={openAddMenuItemVariant || !!editingMenuItemVariant}
        size={'sm'}
        modalHeading={
          openAddMenuItemVariant
            ? `${t('actions.createNew')} ${t('common.variant')}`
            : `${t('actions.update')} ${t('common.variant')}`
        }
        modalLabel={tableTitle}
        primaryButtonText={
          submitting ? `${t('common.loading')}...` : editingMenuItemVariant ? t('actions.update') : t('actions.create')
        }
        secondaryButtonText={t('actions.cancel')}
        onSecondarySubmit={handleCloseAddOrEditMenuItemVariant}
        onRequestClose={handleCloseAddOrEditMenuItemVariant}
        primaryButtonDisabled={submitting}
        onRequestSubmit={() => formRef.current?.submitForm()}
      >
        <MenuItemVariantForm
          errorMessage={createError || updateError}
          initialValues={editingMenuItemVariant}
          ref={formRef}
          onSubmit={handleCreateOrUpdate}
          onSelectedFile={setCroppingFile}
        />
      </Modal>
      <CropperModal
        croppingFile={croppingFile}
        onClose={() => setCroppingFile(null)}
        cropperAspectRatio={1}
        onChange={handleCropped}
      />
      <DeleteConfirmation
        open={!!deletingMenuItemVariant}
        heading={t('messages.areYouSureToDeleteThisVariant')}
        label={tableTitle}
        onClose={handleAbortDelete}
        onDelete={handleDelete}
        deleting={deleteMenuItemVariantMutation.isLoading}
      />
      <ImagesViewer
        images={viewingMenuItemVariantPhotos.map((item) => ({
          src: getImageUrl(item.photos[0])!,
          caption: `${currentRestaurant?.name} - ${item.name} ${t('common.photos')}`,
        }))}
        onClose={() => setViewingMenuItemVariantPhotos([])}
      />
    </>
  );
});

export default MenuItemVariantsSettings;
